import type { CSSProperties } from 'react';

const MAX_SAMPLE_EDGE = 256;
export type TransparencyState = 'transparent' | 'opaque' | 'unknown';

export type TransparencyResult = {
  state: TransparencyState;
  reason?: string;
};

export const UNKNOWN_TRANSPARENCY: TransparencyResult = { state: 'unknown' };

const transparencyResultCache = new Map<string, TransparencyResult>();

function getSampleSize(width: number, height: number) {
  const longest = Math.max(width, height);
  if (longest <= 0) return { width: 1, height: 1 };
  const scale = Math.min(1, MAX_SAMPLE_EDGE / longest);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function detectTransparencyFromSource(
  source: CanvasImageSource,
  width: number,
  height: number
): TransparencyResult {
  const { width: sampleWidth, height: sampleHeight } = getSampleSize(
    width,
    height
  );
  if (sampleWidth <= 0 || sampleHeight <= 0) {
    return { state: 'unknown', reason: 'invalid-sample-size' };
  }

  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;
  const context = canvas.getContext('2d', {
    willReadFrequently: true,
  });
  if (!context) {
    return { state: 'unknown', reason: 'no-canvas-context' };
  }

  try {
    context.clearRect(0, 0, sampleWidth, sampleHeight);
    context.drawImage(source, 0, 0, sampleWidth, sampleHeight);
    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] < 255) return { state: 'transparent' };
    }
    return { state: 'opaque' };
  } catch {
    return { state: 'unknown', reason: 'canvas-read-failed' };
  }
}

function loadImage(
  src: string,
  options: { useCrossOrigin: boolean }
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    if (
      options.useCrossOrigin &&
      !src.startsWith('blob:') &&
      !src.startsWith('data:')
    ) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

export async function detectImageTransparencyFromUrl(
  src: string
): Promise<TransparencyResult> {
  if (!src) return { state: 'unknown', reason: 'empty-src' };

  const attempts = [true, false];
  let lastReason: string | undefined;
  for (const useCrossOrigin of attempts) {
    try {
      const img = await loadImage(src, { useCrossOrigin });
      const result = detectImageTransparencyFromElement(img);
      if (result.state !== 'unknown') {
        return result;
      }
      lastReason = result.reason;
    } catch {
      lastReason = useCrossOrigin
        ? 'image-load-failed-cors'
        : 'image-load-failed';
    }
  }

  try {
    // A normal <img> may cache a response without CORS headers. Revalidate via
    // a fresh CORS request before reading pixels from a local blob.
    const response = await fetch(src, { mode: 'cors', cache: 'reload' });
    if (!response.ok) {
      return {
        state: 'unknown',
        reason: `blob-fetch-failed-${response.status}`,
      };
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const img = await loadImage(blobUrl, { useCrossOrigin: false });
      return detectImageTransparencyFromElement(img);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch {
    return { state: 'unknown', reason: lastReason ?? 'blob-fetch-failed' };
  }
}

export function detectImageTransparencyFromElement(
  image: HTMLImageElement
): TransparencyResult {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (width <= 0 || height <= 0) {
    return { state: 'unknown', reason: 'invalid-natural-size' };
  }
  return detectTransparencyFromSource(image, width, height);
}

function cacheTransparencyResult(
  result: TransparencyResult,
  sources: Array<string | null | undefined>
) {
  if (result.state === 'unknown') return;
  for (const source of sources) {
    if (!source) continue;
    transparencyResultCache.set(source, result);
  }
}

export function readCachedTransparency(
  currentSrc: string | null,
  fallbackSrc?: string
): TransparencyResult {
  if (currentSrc) {
    const cachedCurrent = transparencyResultCache.get(currentSrc);
    if (cachedCurrent && cachedCurrent.state !== 'unknown') {
      return cachedCurrent;
    }
  }
  if (fallbackSrc) {
    const cachedFallback = transparencyResultCache.get(fallbackSrc);
    if (cachedFallback && cachedFallback.state !== 'unknown') {
      return cachedFallback;
    }
  }
  return UNKNOWN_TRANSPARENCY;
}

export function detectKnownImageTransparency(params: {
  image?: HTMLImageElement | null;
  currentSrc?: string | null;
  fallbackSrc?: string;
}): TransparencyResult {
  const { image, fallbackSrc } = params;
  const currentSrc =
    params.currentSrc ?? image?.currentSrc ?? image?.src ?? null;
  const cached = readCachedTransparency(currentSrc, fallbackSrc);
  if (cached.state !== 'unknown') {
    return cached;
  }
  if (!image) {
    return UNKNOWN_TRANSPARENCY;
  }
  const immediate = detectImageTransparencyFromElement(image);
  cacheTransparencyResult(immediate, [currentSrc, fallbackSrc]);
  return immediate;
}

export async function resolveImageTransparencyWithFallback(params: {
  currentSrc?: string | null;
  fallbackSrc?: string;
}): Promise<TransparencyResult> {
  const { fallbackSrc } = params;
  const currentSrc = params.currentSrc ?? null;
  const cached = readCachedTransparency(currentSrc, fallbackSrc);
  if (cached.state !== 'unknown') {
    return cached;
  }

  if (currentSrc) {
    const initialResult = await detectImageTransparencyFromUrl(currentSrc);
    if (initialResult.state !== 'unknown') {
      cacheTransparencyResult(initialResult, [currentSrc, fallbackSrc]);
      return initialResult;
    }
  }

  if (fallbackSrc && fallbackSrc !== currentSrc) {
    const fallbackResult = await detectImageTransparencyFromUrl(fallbackSrc);
    cacheTransparencyResult(fallbackResult, [currentSrc, fallbackSrc]);
    return fallbackResult;
  }

  return UNKNOWN_TRANSPARENCY;
}

export const CHECKERBOARD_BACKGROUND_STYLE: CSSProperties = {
  backgroundColor: 'var(--cms-image-checkerboard-base)',
  backgroundImage: 'var(--cms-image-checkerboard-background-image)',
  backgroundPosition: 'var(--cms-image-checkerboard-background-position)',
  backgroundSize: 'var(--cms-image-checkerboard-background-size)',
};
